import React, { useEffect } from "react";
import { Input } from "components/RFQ/Input";
import { Button } from "newui/components/Button";
import { Checkbox } from "components/RFQ/Checkbox";
import { saveQuestionnareTemplate } from "actions/RFQ/templateAction";

import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Spinner,
} from "@chakra-ui/react";
import { CloseButton } from "components/RFQ/CloseButton";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { RFxInvitationEmailContent } from "components/email-template/RFxInvitationEmailContent";
import { RfxMail } from "components/email-template/types/RfxMail";
import { OnboardingRFxInvitationEmailContent } from "components/email-template/Onboarding/OnboardingRFxInvitationEmailContent";
import { isRfxOnboardingType } from "newui/features/Rfx/utils";
import { useCurrentCompany } from "hooks/auth";

const SubmitRFQModal = ({
	isOpen,
	setIsOpen,
	rfqId,
	isSaving,
	isSubmitting,
	handleSubmit,
	rfqDetail,
	companyName,
	props,
}) => {
	const [checked, setChecked] = React.useState(false);
	const [templateName, setTemplateName] = React.useState("");
	const [isLoading, setIsLoading] = React.useState(false);
	const [additionalMessage, setAdditionalMessage] = React.useState("");
	const isEmpty = templateName === "";
	const company = useCurrentCompany();
	const [rfxMail, setRfxMail] = React.useState("");

	const onClose = () => {
		setIsOpen(false);
	};

	const onClickSendHandler = async () => {
		if (checked && !isEmpty) {
			setIsLoading(true);
			if (!isEmpty) {
				const saved = await saveQuestionnareTemplate(rfqId, templateName);
				if (saved) {
					handleSubmit("submit", additionalMessage).finally(() =>
						setIsLoading(false)
					);
				}
				setTemplateName("");
			}
		} else {
			handleSubmit("submit", additionalMessage);
		}
	};

	const FooterComponent = () => {
		return (
			<div className="flex w-full justify-between gap-2">
				<Button
					onClick={onClose}
					btntype="base"
					className="!w-full"
				>
					Cancel
				</Button>
				<Button
					variant="primary"
					className="flex !w-full gap-3"
					disabled={
						(checked && isEmpty) || isLoading || isSubmitting || isSaving
					}
					onClick={onClickSendHandler}
					data-testid="send-rfq"
				>
					{isLoading ? "Saving template.." : isSaving ? "Sending RFx" : "Send"}
					{(isLoading || isSubmitting || isSaving) && (
						<Spinner
							width={4}
							height={4}
						/>
					)}
				</Button>
			</div>
		);
	};

	useEffect(() => {
		if (!rfqDetail || !company) {
			return;
		}

		/** @type {import("components/email-template/types/RfxMail").RfxMail} */
		let newMail = new RfxMail(rfqDetail, company, null, additionalMessage);
		setRfxMail(newMail);
	}, [rfqDetail, rfqDetail.rfxLead, additionalMessage, companyName, company]);

	const isOnboardingRFx = isRfxOnboardingType(
		rfqDetail?.rfxType || rfqDetail?.type
	);

	return (
		<Drawer
			placement={"right"}
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
			{...props}
			className="bg-white p-11"
		>
			<DrawerOverlay onClick={onClose} />
			<DrawerContent maxWidth={"1280px"}>
				<DrawerHeader>
					<Flex
						justifyContent="space-between"
						align={"center"}
					>
						<div>
							<p className="text-lg font-semibold text-primary-900">
								RFx Invitation Email Preview
							</p>
						</div>
						<CloseButton onClose={onClose} />
					</Flex>
				</DrawerHeader>
				<DrawerBody>
					<div className="grid grid-cols-3">
						<div className="space-y-4 py-4">
							<div className="flex flex-col gap-2">
								<label className="text-sm font-medium">
									Additional Message (Optional)
								</label>
								<Input
									value={additionalMessage}
									onChange={(e) => setAdditionalMessage(e.target.value)}
									placeholder="Add a message to your invitation email"
									className="w-full"
								/>
							</div>
							<div className="flex flex-col gap-2">
								<Checkbox
									checked={checked}
									onChange={(e) => setChecked(e.target.checked)}
									label="Save this RFx as a template"
								/>
								{checked && (
									<Input
										value={templateName}
										onChange={(e) => setTemplateName(e.target.value)}
										placeholder="Template Name"
										className="w-full"
									/>
								)}
							</div>
							<FooterComponent />
						</div>
						<div className="col-span-2">
							<EmailPreview>
								{isOnboardingRFx ? (
									<OnboardingRFxInvitationEmailContent rfxMail={rfxMail} />
								) : (
									<RFxInvitationEmailContent rfxMail={rfxMail} />
								)}
							</EmailPreview>
						</div>
					</div>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};

export default SubmitRFQModal;
